import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import styled, { keyframes, css, createGlobalStyle } from 'styled-components'

/* ════════════════════════════════════════════════════════════
   TOKENS — direction « Atelier »
   Blanc pur, filets fins, typo serrée, un seul accent vert profond.
   Pas d'ombre portée : la hiérarchie vient du trait et du contraste.
   ════════════════════════════════════════════════════════════ */
export const t = {
  /* Fonds */
  bg: '#FBFBFA',
  surface: '#FFFFFF',
  surfaceAlt: '#FBFBFA',
  surfaceSunk: '#F4F5F2',
  surfaceLift: '#FFFFFF',

  /* Compat ancienne nomenclature « sombre » */
  ink: '#16181C',
  inkSoft: '#2A2D31',
  inkLift: '#3A3E42',
  inkLine: '#ECEDEA',
  onInk: '#FFFFFF',
  onInkSoft: 'rgba(255,255,255,0.72)',
  onInkFaint: 'rgba(255,255,255,0.45)',

  /* Texte */
  text: '#16181C',
  textSoft: '#62676B',
  textFaint: '#8A8F93',

  /* Traits */
  line: '#ECEDEA',
  lineSoft: '#F2F3F0',

  /* Accent unique — vert profond */
  gold: '#0F5C4D',
  goldLight: '#157A66',
  goldDeep: '#0B4539',
  goldWash: '#E7F1EE',

  /* Sémantique */
  green: '#146138',
  greenLight: '#1B7A46',
  greenWash: '#E9F4EC',
  blue: '#1E5E96',
  blueWash: '#EAF3FB',
  amber: '#8A5A12',
  amberWash: '#FDF2E3',
  red: '#B4321F',
  redWash: '#FCEDEA',
  teal: '#0F6E72',
  tealWash: '#E6F3F4',

  /* Formes */
  r: '10px',
  rSm: '8px',
  rXs: '5px',
  font: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  serif: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',

  shadow: 'none',
  shadowLift: '0 1px 2px rgba(22,24,28,0.05), 0 8px 22px rgba(22,24,28,0.07)',
  glowGold: '0 0 0 1px rgba(15,92,77,0.18)',
}

/* Fond de page pendant que l'admin est monté */
export const AdminGlobal = createGlobalStyle`
  body {
    background: ${t.bg};
    padding-top: 0 !important;
  }
  ::selection { background: ${t.goldWash}; color: ${t.goldDeep}; }
`

/* Rendu neutre : la direction Atelier n'a pas de décor de fond. */
export function Aurora() { return null }

/* ── Animations : sobres et rapides ── */
const ease = 'cubic-bezier(.22,.9,.3,1)'

export const fadeUp = keyframes`from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}`
export const fadeIn = keyframes`from{opacity:0}to{opacity:1}`
export const scaleIn = keyframes`from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:translateY(0) scale(1)}`
export const slideLeft = keyframes`from{opacity:0;transform:translateX(10px)}to{opacity:1;transform:translateX(0)}`
export const shimmerMove = keyframes`0%{background-position:-420px 0}100%{background-position:420px 0}`
export const spinKf = keyframes`to{transform:rotate(360deg)}`
export const popIn = keyframes`from{opacity:0;transform:scale(.9)}to{opacity:1;transform:scale(1)}`

export const Stagger = styled.div`
  animation: ${fadeUp} 0.26s ${ease} both;
  animation-delay: ${(p) => (p.$i ? Math.min(p.$i, 12) * 22 : 0)}ms;
`

/* ════════════════════════════════════════════════════════════
   SURFACES
   ════════════════════════════════════════════════════════════ */
export const Panel = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: ${t.r};
  padding: ${(p) => p.$pad || '20px'};
  ${(p) => p.$accent && css`
    border-color: ${t.gold};
    box-shadow: inset 0 0 0 1px rgba(15,92,77,0.06);
  `}
`
export const PanelHead = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  gap: 14px; flex-wrap: wrap;
  margin-bottom: ${(p) => (p.$flush ? '0' : '16px')};
`
export const PanelTitle = styled.h3`
  font-family: ${t.serif};
  font-size: ${(p) => (p.$sm ? '0.92rem' : '1.02rem')};
  font-weight: 640; letter-spacing: -0.026em;
  color: ${t.text}; margin: 0;
  display: flex; align-items: center; gap: 8px;
`
export const Muted = styled.p`
  font-family: ${t.font}; font-size: 0.82rem; color: ${t.textSoft};
  line-height: 1.55; margin: 0;
`

/* ── Boutons ── */
const btnBase = css`
  font-family: ${t.font}; font-weight: 560; letter-spacing: -0.006em;
  cursor: pointer; border: 1px solid transparent; border-radius: ${t.rSm};
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  white-space: nowrap;
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  /* Les boutons de l'Atelier n'ont pas de contour propre : sans cet anneau,
     le parcours au clavier devient invisible. Même halo que les champs, pour
     que le focus se lise partout de la même façon. */
  &:focus-visible {
    outline: none;
    border-color: ${t.gold};
    box-shadow: 0 0 0 3px rgba(15,92,77,0.16);
  }
  &:disabled { opacity: 0.38; cursor: not-allowed; }
`
const sizes = {
  sm: css`padding: 5px 11px; font-size: 0.735rem;`,
  md: css`padding: 7px 14px; font-size: 0.785rem;`,
  lg: css`padding: 10px 19px; font-size: 0.84rem;`,
}

export const Btn = styled.button`
  ${btnBase}
  ${(p) => sizes[p.$size || 'md']}

  ${(p) => {
    switch (p.$variant) {
      case 'gold':
        return css`
          background: ${t.gold}; color: #fff;
          &:hover:not(:disabled) { background: ${t.goldDeep}; }
        `
      case 'ghost':
        return css`
          background: ${t.surface}; color: ${t.text}; border-color: ${t.line};
          &:hover:not(:disabled) { background: ${t.surfaceSunk}; border-color: #DDDFDA; }
        `
      case 'quiet':
        return css`
          background: transparent; color: ${t.textSoft};
          &:hover:not(:disabled) { background: ${t.surfaceSunk}; color: ${t.text}; }
        `
      case 'danger':
        return css`
          background: ${t.surface}; color: ${t.red}; border-color: #F0D8D3;
          &:hover:not(:disabled) { background: ${t.redWash}; border-color: ${t.red}; }
        `
      case 'dangerSolid':
        return css`
          background: ${t.red}; color: #fff;
          &:hover:not(:disabled) { background: #96291A; }
        `
      default:
        return css`
          background: ${t.gold}; color: #fff;
          &:hover:not(:disabled) { background: ${t.goldDeep}; }
        `
    }
  }}
  ${(p) => p.$block && css`width: 100%;`}
`

export const IconBtn = styled.button`
  ${btnBase}
  padding: 0;
  width: ${(p) => (p.$sm ? '28px' : '32px')};
  height: ${(p) => (p.$sm ? '28px' : '32px')};
  font-size: ${(p) => (p.$sm ? '0.78rem' : '0.86rem')};
  background: ${t.surface}; color: ${t.textSoft}; border-color: ${t.line};
  &:hover:not(:disabled) { background: ${t.surfaceSunk}; color: ${t.text}; }
`

/* ── Formulaires ── */
const fieldBase = css`
  width: 100%;
  font-family: ${t.font}; font-size: 0.835rem; letter-spacing: -0.008em;
  color: ${t.text}; background: ${t.surface};
  border: 1px solid #E0E2DE;
  border-radius: ${t.rSm};
  padding: 8px 11px;
  outline: none;
  transition: border-color 0.14s, box-shadow 0.14s;
  &::placeholder { color: ${t.textFaint}; }
  &:focus { border-color: ${t.gold}; box-shadow: 0 0 0 3px rgba(15,92,77,0.1); }
  &:disabled { background: ${t.surfaceSunk}; color: ${t.textFaint}; cursor: not-allowed; }
`
export const Input = styled.input`${fieldBase}`
export const Textarea = styled.textarea`${fieldBase} resize: vertical; min-height: 88px; line-height: 1.55;`
export const Select = styled.select`${fieldBase} cursor: pointer;`

export const Label = styled.label`
  display: block;
  font-family: ${t.font}; font-size: 0.635rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase;
  color: ${t.textFaint}; margin-bottom: 6px;
`
export const Field = styled.div`margin-bottom: ${(p) => (p.$flush ? 0 : '14px')};`

export const Row = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || '1fr'};
  gap: ${(p) => p.$gap || '12px'};
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`
export const Cluster = styled.div`
  display: flex; align-items: ${(p) => p.$align || 'center'};
  flex-wrap: wrap; gap: ${(p) => p.$gap || '8px'};
  ${(p) => p.$end && css`justify-content: flex-end;`}
`

/* ── Pastilles ── */
const TONES = {
  neutral: [t.surfaceSunk, t.textSoft, '#E6E8E3'],
  gold: [t.goldWash, t.gold, '#CFE2DC'],
  green: [t.greenWash, t.green, '#CFE6D8'],
  blue: [t.blueWash, t.blue, '#D3E4F3'],
  amber: [t.amberWash, t.amber, '#F0DFC2'],
  red: [t.redWash, t.red, '#F0D8D3'],
  teal: [t.tealWash, t.teal, '#CFE5E6'],
}

export const Pill = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  padding: ${(p) => (p.$lg ? '3px 10px' : '2px 8px')};
  border-radius: ${t.rXs};
  font-family: ${t.font};
  font-size: ${(p) => (p.$lg ? '0.735rem' : '0.685rem')};
  font-weight: 560; letter-spacing: -0.004em; white-space: nowrap;
  background: ${(p) => (TONES[p.$tone] || TONES.neutral)[0]};
  color: ${(p) => (TONES[p.$tone] || TONES.neutral)[1]};
`
export const Dot = styled.span`
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
  background: ${(p) => (TONES[p.$tone] || TONES.neutral)[1]};
`
/* Pastille de compteur. $tone la colore selon la nature de l'alerte ;
   l'icône passée en enfant dit de quoi il s'agit sans survol. */
export const CountBadge = styled.span`
  height: 18px; padding: 0 6px; border-radius: ${t.rXs};
  background: ${(p) => (TONES[p.$tone] || TONES.red)[0]};
  color: ${(p) => (TONES[p.$tone] || TONES.red)[1]};
  border: 1px solid ${(p) => (TONES[p.$tone] || TONES.red)[2]};
  font-family: ${t.font}; font-size: 0.635rem; font-weight: 620;
  font-variant-numeric: tabular-nums; white-space: nowrap;
  display: inline-flex; align-items: center; gap: 3px;
  flex-shrink: 0;
  b { font-size: 0.7rem; line-height: 1; font-weight: 400; }
`

/* ── Filtres ── */
export const ChipBar = styled.div`
  display: flex; gap: 6px; flex-wrap: wrap;
  ${(p) => p.$scroll && css`
    @media (max-width: 700px) {
      flex-wrap: nowrap; overflow-x: auto; padding-bottom: 4px;
      -webkit-overflow-scrolling: touch;
      &::-webkit-scrollbar { display: none; }
    }
  `}
`
export const Chip = styled.button`
  font-family: ${t.font}; font-size: 0.745rem; font-weight: 520;
  letter-spacing: -0.008em;
  padding: 5px 11px; border-radius: ${t.rSm}; cursor: pointer; white-space: nowrap;
  transition: background 0.14s, border-color 0.14s, color 0.14s;
  background: ${(p) => (p.$on ? t.gold : t.surface)};
  color: ${(p) => (p.$on ? '#fff' : t.textSoft)};
  border: 1px solid ${(p) => (p.$on ? t.gold : t.line)};
  &:hover { border-color: ${(p) => (p.$on ? t.goldDeep : '#DDDFDA')}; color: ${(p) => (p.$on ? '#fff' : t.text)}; }
  span { opacity: 0.6; margin-left: 3px; font-variant-numeric: tabular-nums; }
`

/* ════════════════════════════════════════════════════════════
   TUILES — chiffres alignés, pas d'effet
   ════════════════════════════════════════════════════════════ */
export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$n || 4}, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 460px) { grid-template-columns: 1fr; }
`
const TileWrap = styled.div`
  height: 100%;
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: ${t.r};
  padding: 12px 14px;
  transition: border-color 0.14s;
  &:hover { border-color: #DDDFDA; }
`
const TileLabel = styled.div`
  font-family: ${t.font}; font-size: 0.625rem; font-weight: 600;
  letter-spacing: 0.075em; text-transform: uppercase; color: ${t.textFaint};
`
const TileValue = styled.div`
  font-family: ${t.serif}; font-size: 1.32rem; font-weight: 650;
  letter-spacing: -0.038em; line-height: 1.15; margin-top: 5px;
  font-variant-numeric: tabular-nums;
  color: ${t.text};
`
const TileHint = styled.div`
  font-family: ${t.font}; font-size: 0.68rem; font-weight: 540; margin-top: 3px;
  color: ${(p) => (TONES[p.$tone] || TONES.gold)[1]};
`
export function Tile({ label, value, hint, tone = 'gold', i }) {
  return (
    <Stagger $i={i} style={{ height: '100%' }}>
      <TileWrap>
        <TileLabel>{label}</TileLabel>
        <TileValue>{value}</TileValue>
        {hint && <TileHint $tone={tone}>{hint}</TileHint>}
      </TileWrap>
    </Stagger>
  )
}

/* ── États vides / chargement ── */
const EmptyWrap = styled.div`
  text-align: center; padding: 46px 24px;
  border: 1px dashed #E0E2DE;
  border-radius: ${t.r};
  background: ${t.surfaceAlt};
`
const EmptyIcon = styled.div`font-size: 1.6rem; margin-bottom: 9px; opacity: 0.6;`
const EmptyTitle = styled.div`
  font-family: ${t.serif}; font-size: 0.95rem; font-weight: 620;
  letter-spacing: -0.022em; color: ${t.text}; margin-bottom: 5px;
`
export function Empty({ icon = '🗂️', title, children }) {
  return (
    <EmptyWrap>
      <EmptyIcon>{icon}</EmptyIcon>
      <EmptyTitle>{title}</EmptyTitle>
      {children && <Muted>{children}</Muted>}
    </EmptyWrap>
  )
}

export const Skeleton = styled.div`
  height: ${(p) => p.$h || '64px'};
  border-radius: ${(p) => p.$r || t.r};
  background: linear-gradient(90deg, ${t.surfaceSunk} 25%, #FAFBF8 50%, ${t.surfaceSunk} 75%);
  background-size: 420px 100%;
  animation: ${shimmerMove} 1.3s linear infinite;
  margin-bottom: ${(p) => p.$mb || '8px'};
`
export const Spinner = styled.span`
  width: ${(p) => p.$sz || '14px'}; height: ${(p) => p.$sz || '14px'};
  border: 2px solid currentColor; border-right-color: transparent;
  border-radius: 50%; display: inline-block;
  animation: ${spinKf} 0.6s linear infinite;
  vertical-align: -2px;
`
export function Loading({ rows = 3, h }) {
  return <div>{Array.from({ length: rows }).map((_, i) => <Skeleton key={i} $h={h} />)}</div>
}

/* ── Listes ── */
export const ListItem = styled.button`
  position: relative;
  width: 100%; text-align: left; display: block;
  background: ${(p) => (p.$on ? t.goldWash : t.surface)};
  border: 1px solid ${(p) => (p.$on ? t.gold : t.line)};
  border-radius: ${t.r};
  padding: 12px 14px;
  margin-bottom: 7px;
  cursor: pointer;
  font-family: ${t.font};
  transition: border-color 0.14s, background 0.14s;

  /* $mark : état d'avancement côté admin.
     'new'  = jamais ouvert   'todo' = ouvert mais rien fait dessus
     Le liseré gauche est réservé à cet axe ; l'activité du client
     s'affiche séparément dans le corps de la ligne. */
  ${(p) => p.$mark === 'new' && !p.$on && css`
    border-color: #CFE2DC;
    border-left: 3px solid ${t.gold};
    background: ${t.goldWash};
  `}
  ${(p) => p.$mark === 'todo' && !p.$on && css`
    border-color: #F0DFC2;
    border-left: 3px solid ${t.amber};
    background: ${t.amberWash};
  `}

  &:hover {
    border-color: ${(p) => (p.$on ? t.gold : p.$mark === 'new' ? '#B9D5CD' : p.$mark === 'todo' ? '#E4CDA4' : '#DDDFDA')};
    background: ${(p) => (
      p.$on ? t.goldWash
      : p.$mark === 'new' ? t.goldWash
      : p.$mark === 'todo' ? t.amberWash
      : t.surfaceAlt
    )};
    ${(p) => p.$mark === 'new' && css`border-left-color: ${t.gold};`}
    ${(p) => p.$mark === 'todo' && css`border-left-color: ${t.amber};`}
  }
`
export const ItemTitle = styled.div`
  font-family: ${t.font}; font-weight: 570; font-size: 0.855rem;
  letter-spacing: -0.016em; color: ${t.text};
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
export const ItemSub = styled.div`
  font-family: ${t.font}; font-size: 0.75rem; color: ${t.textSoft}; margin-top: 2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`
export const ItemMeta = styled.div`
  font-family: ${t.font}; font-size: 0.7rem; color: ${t.textFaint}; margin-top: 4px;
  font-variant-numeric: tabular-nums;
`

/* ── Paires clé/valeur ── */
export const DefList = styled.div`
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
  padding: 0 13px;
`
export const DefRow = styled.div`
  display: flex; justify-content: space-between; gap: 14px;
  padding: 9px 0;
  border-bottom: 1px solid ${t.lineSoft};
  &:last-child { border-bottom: none; }
  dt { font-family: ${t.font}; font-size: 0.745rem; color: ${t.textFaint}; margin: 0; flex-shrink: 0; }
  dd {
    font-family: ${t.font}; font-size: 0.82rem; font-weight: 540; letter-spacing: -0.01em;
    color: ${t.text}; margin: 0; text-align: right; white-space: pre-wrap;
  }
`
export const Pre = styled.pre`
  font-family: ${t.mono};
  font-size: 0.715rem; line-height: 1.65; color: ${t.textSoft};
  background: ${(p) => (p.$tone === 'green' ? t.greenWash : t.surfaceAlt)};
  border: 1px solid ${(p) => (p.$tone === 'green' ? '#CFE6D8' : t.line)};
  border-radius: ${t.rSm};
  padding: 12px 14px;
  white-space: pre-wrap; overflow-y: auto;
  max-height: ${(p) => p.$max || '260px'};
  margin: 0;
`

/* ── En-tête de page ── */
export const PageHead = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 18px; flex-wrap: wrap;
`
export const PageTitle = styled.h1`
  font-family: ${t.serif}; font-size: 1.32rem; font-weight: 660;
  letter-spacing: -0.036em; color: ${t.text}; margin: 0;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  @media (max-width: 700px) { font-size: 1.14rem; }
`
export const PageSub = styled.p`
  font-family: ${t.font}; font-size: 0.775rem; color: ${t.textFaint};
  margin: 3px 0 0; font-variant-numeric: tabular-nums;
`
export const BackLink = styled.button`
  background: none; border: none; cursor: pointer;
  font-family: ${t.font}; font-size: 0.765rem; font-weight: 530; color: ${t.textSoft};
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 0; margin-bottom: 12px;
  transition: color 0.14s;
  &:hover { color: ${t.gold}; }
`

/* ════════════════════════════════════════════════════════════
   POP-UPS
   ════════════════════════════════════════════════════════════ */
const Scrim = styled.div`
  position: fixed; inset: 0; z-index: 4000;
  background: rgba(22,24,28,0.32);
  display: flex; align-items: center; justify-content: center; padding: 22px;
  animation: ${fadeIn} 0.16s ease both;
`
const Dialog = styled.div`
  width: 100%; max-width: ${(p) => p.$w || '420px'};
  background: ${t.surface};
  border: 1px solid ${t.line};
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(22,24,28,0.16);
  padding: 24px;
  animation: ${scaleIn} 0.2s ${ease} both;
  max-height: 88vh; overflow-y: auto;
`
const DialogIcon = styled.div`
  width: 38px; height: 38px; border-radius: ${t.rSm};
  display: flex; align-items: center; justify-content: center;
  font-size: 1.05rem; margin-bottom: 14px;
  background: ${(p) => (TONES[p.$tone] || TONES.gold)[0]};
`
const DialogTitle = styled.h3`
  font-family: ${t.serif}; font-size: 1.04rem; font-weight: 650;
  letter-spacing: -0.028em; color: ${t.text}; margin: 0 0 7px;
`
const DialogBody = styled.div`
  font-family: ${t.font}; font-size: 0.83rem; line-height: 1.6;
  color: ${t.textSoft}; margin-bottom: 20px;
  strong { color: ${t.text}; font-weight: 600; }
`
const DialogActions = styled.div`
  display: flex; gap: 8px; justify-content: flex-end;
  @media (max-width: 460px) { flex-direction: column-reverse; ${Btn} { width: 100%; } }
`
const ToastStack = styled.div`
  position: fixed; z-index: 5000; bottom: 20px; right: 20px;
  display: flex; flex-direction: column-reverse; gap: 8px; pointer-events: none;
  @media (max-width: 640px) { left: 14px; right: 14px; bottom: 14px; }
`
const ToastCard = styled.div`
  pointer-events: auto;
  display: flex; align-items: center; gap: 10px;
  min-width: 230px; max-width: 380px;
  background: ${t.ink}; color: #fff;
  border-radius: ${t.rSm};
  padding: 10px 14px;
  font-family: ${t.font}; font-size: 0.8rem; font-weight: 500;
  letter-spacing: -0.008em;
  box-shadow: 0 8px 26px rgba(22,24,28,0.22);
  animation: ${slideLeft} 0.2s ${ease} both;
`
const ToastGlyph = styled.span`
  width: 17px; height: 17px; border-radius: ${t.rXs}; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.66rem; font-weight: 700;
  background: ${(p) => (p.$tone === 'error' ? '#E8705C' : p.$tone === 'info' ? '#7FA8A0' : '#4FA97A')};
  color: ${t.ink};
`

const AdminUI = createContext(null)

export function useUI() {
  const ctx = useContext(AdminUI)
  if (!ctx) throw new Error('useUI doit être utilisé dans <AdminUIProvider>')
  return ctx
}

export function AdminUIProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [dialog, setDialog] = useState(null)
  const resolver = useRef(null)
  const seq = useRef(0)

  const toast = useCallback((msg, tone = 'success') => {
    // Compat : l'ancien code appelait toast(msg, true) pour une erreur
    const resolved = tone === true ? 'error' : tone === false ? 'success' : tone
    const id = ++seq.current
    setToasts((list) => [...list, { id, msg, tone: resolved }])
    setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), 3600)
  }, [])

  const close = useCallback((result) => {
    setDialog(null)
    if (resolver.current) { resolver.current(result); resolver.current = null }
  }, [])

  const confirm = useCallback((opts) => {
    setDialog({ mode: 'confirm', tone: 'red', icon: '⚠️', confirmLabel: 'Confirmer', cancelLabel: 'Annuler', ...opts })
    return new Promise((res) => { resolver.current = res })
  }, [])

  const announce = useCallback((opts) => {
    setDialog({ mode: 'announce', tone: 'green', icon: '✅', confirmLabel: 'Parfait', ...opts })
    return new Promise((res) => { resolver.current = res })
  }, [])

  useEffect(() => {
    if (!dialog) return
    const onKey = (e) => { if (e.key === 'Escape') close(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dialog, close])

  return (
    <AdminUI.Provider value={{ toast, confirm, announce }}>
      {children}

      {dialog && (
        <Scrim onMouseDown={(e) => { if (e.target === e.currentTarget) close(false) }}>
          <Dialog role="dialog" aria-modal="true" $w={dialog.width}>
            <DialogIcon $tone={dialog.tone}>{dialog.icon}</DialogIcon>
            <DialogTitle>{dialog.title}</DialogTitle>
            {dialog.body && <DialogBody>{dialog.body}</DialogBody>}
            <DialogActions>
              {dialog.mode === 'confirm' && (
                <Btn $variant="ghost" $size="lg" onClick={() => close(false)}>{dialog.cancelLabel}</Btn>
              )}
              <Btn
                $size="lg"
                $variant={dialog.tone === 'red' ? 'dangerSolid' : 'gold'}
                onClick={() => close(true)}
                autoFocus
              >
                {dialog.confirmLabel}
              </Btn>
            </DialogActions>
          </Dialog>
        </Scrim>
      )}

      <ToastStack>
        {toasts.map((x) => (
          <ToastCard key={x.id}>
            <ToastGlyph $tone={x.tone}>{x.tone === 'error' ? '!' : x.tone === 'info' ? 'i' : '✓'}</ToastGlyph>
            <span>{x.msg}</span>
          </ToastCard>
        ))}
      </ToastStack>
    </AdminUI.Provider>
  )
}

/* ── Tableaux de chiffres ────────────────────────────────────
   Défilent horizontalement dans leur propre cadre : sur un iPhone,
   la page entière ne doit jamais partir de travers. */
export const TableWrap = styled.div`
  overflow-x: auto;
  border: 1px solid ${t.line};
  border-radius: ${t.rSm};
`
export const Tableau = styled.table`
  width: 100%; min-width: ${(p) => p.$min || '720px'};
  border-collapse: collapse;
  font-family: ${t.font}; font-size: 0.79rem;
  th, td { padding: 9px 12px; text-align: left; white-space: nowrap; }
  th {
    font-size: 0.62rem; font-weight: 600; letter-spacing: 0.075em;
    text-transform: uppercase; color: ${t.textFaint};
    background: ${t.surfaceAlt}; border-bottom: 1px solid ${t.line};
    position: sticky; top: 0; z-index: 1;
  }
  td { border-bottom: 1px solid ${t.lineSoft}; color: ${t.textSoft}; }
  tbody tr:last-child td { border-bottom: none; }
  tfoot td {
    border-top: 2px solid ${t.line}; border-bottom: none;
    font-weight: 640; color: ${t.text};
  }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .fort { color: ${t.text}; font-weight: 560; }
`
